import { useNavigate } from 'react-router-dom';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Settings2, FolderKanban, User, ChevronRight, FileText, Bell, TrendingUp, } from 'lucide-react';

interface SettingsCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

function SettingsCard({ title, description, icon, onClick }: SettingsCardProps) {
  return (
    <Card 
      className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50 group"
      onClick={onClick}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
              {icon}
            </div>
            <div>
              <CardTitle className="text-xl">{title}</CardTitle>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
        <CardDescription className="mt-2">{description}</CardDescription>
      </CardHeader>
    </Card>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="sticky top-0 z-40 bg-background border-b shadow-sm">
        <div className="max-w-5xl mx-auto p-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground">
              Manage your application settings and preferences
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <SettingsCard
              title="General Settings"
              description="Configure general application settings and system preferences"
              icon={<Settings2 className="h-6 w-6 text-primary" />}
              onClick={() => navigate('/settings/general')}
            />
            
            <SettingsCard
              title="Project Settings"
              description="Manage project creation, templates, budgets, and field constraints"
              icon={<FolderKanban className="h-6 w-6 text-primary" />}
              onClick={() => navigate('/settings/project')}
            />
            
            <SettingsCard
              title="User Preferences"
              description="Customize your personal preferences, appearance, and accessibility options"
              icon={<User className="h-6 w-6 text-primary" />}
              onClick={() => navigate('/settings/user-preferences')}
            />
            
            <SettingsCard
              title="Templates"
              description="Create and manage project templates for streamlined project creation"
              icon={<FileText className="h-6 w-6 text-primary" />}
              onClick={() => navigate('/settings/templates')}
            />
            
            <SettingsCard
              title="Finance Settings"
              description="Configure GP calculation methods and company-wide finance preferences"
              icon={<TrendingUp className="h-6 w-6 text-primary" />}
              onClick={() => navigate('/settings/finance')}
            />

            <SettingsCard
              title="Notifications"
              description="Configure how and when users are notified about project-related events"
              icon={<Bell className="h-6 w-6 text-primary" />}
              onClick={() => navigate('/settings/notifications')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
